import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AjaxService } from 'src/app/service/ajax.service';
import { API } from 'src/environments/environment';
import { UtilService } from 'src/app/service/util.service';

interface Pergunta {
  id: number;
  descricao: string;
}

interface PlanoAcao {
  id?: number;
  what: string;      // O que será feito?
  why: string;       // Por que será feito?
  where: string;     // Onde será feito?
  when: Date;        // Quando será feito?
  who: string;       // Quem fará?
  how: string;       // Como será feito?
  howMuch: number;   // Quanto custará?
  status: string;    // Status do plano
  perguntaId: number;  // ID da pergunta que gerou o plano
  pergunta: string;  // Descrição da pergunta
  dataCriacao: Date;
  dataAtualizacao?: Date;
}

@Component({
  selector: 'app-plano-acao',
  templateUrl: './plano-acao.component.html',
  styleUrls: ['./plano-acao.component.scss']
})
export class PlanoAcaoComponent implements OnInit {
  planosAcao: PlanoAcao[] = [];
  perguntas: Pergunta[] = [];
  planoAcaoForm: FormGroup;
  planoSelecionado: PlanoAcao | null = null;
  dtOptions: DataTables.Settings = {};

  constructor(
    private fb: FormBuilder,
    public modalService: NgbModal,
    private ajax: AjaxService,
    private util: UtilService
  ) {
    this.planoAcaoForm = this.fb.group({
      perguntaId: ['', [Validators.required]],
      what: ['', [Validators.required]],
      why: ['', [Validators.required]],
      where: ['', [Validators.required]],
      when: ['', [Validators.required]],
      who: ['', [Validators.required]],
      how: ['', [Validators.required]],
      howMuch: ['', [Validators.required, Validators.min(0)]],
      status: ['Pendente']
    });
  }

  ngOnInit(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 10,
      processing: true,
      language: {
        url: '//cdn.datatables.net/plug-ins/1.13.7/i18n/pt-BR.json'
      }
    };
    this.carregarPerguntas();
  }

  carregarPerguntas() {
    this.util.showLoading();
    this.ajax.get(`${API}Pergunta/Pergunta?IS_RESUMIR_AGRUPAMENTOS=true`)
      .subscribe({
        next: (response) => {
          this.util.closeLoading();
          if (response && response.data) {
            this.perguntas = response.data;
          } else {
            this.perguntas = [];
          }
        },
        error: (error) => {
          this.util.closeLoading();
          this.util.toasterShowError("Erro", "Erro ao carregar perguntas. Tente novamente.");
          console.error('Erro ao carregar perguntas:', error);
        }
      });
  }

  carregarPlanosAcao() {
    this.util.showLoading();
    this.ajax.get(`${API}PlanoAcao/PlanoAcao`)
      .subscribe({
        next: (response) => {
          this.util.closeLoading();
          if (response && response.data) {
            this.planosAcao = response.data.map((plano: any) => ({
              id: plano.id,
              what: plano.what,
              why: plano.why,
              where: plano.where,
              when: new Date(plano.when),
              who: plano.who,
              how: plano.how,
              howMuch: plano.howMuch,
              status: plano.status,
              perguntaId: plano.perguntaId,
              pergunta: plano.pergunta,
              dataCriacao: new Date(plano.dataCriacao),
              dataAtualizacao: plano.dataAtualizacao ? new Date(plano.dataAtualizacao) : undefined
            }));
          } else {
            this.planosAcao = [];
          }
        },
        error: (error) => {
          this.util.closeLoading();
          this.util.toasterShowError("Erro", "Erro ao carregar planos de ação. Tente novamente.");
          console.error('Erro ao carregar planos de ação:', error);
        }
      });
  }

  abrirModalPlanoAcao(modal: any, plano?: PlanoAcao, pergunta?: Pergunta) {
    if (plano) {
      this.planoSelecionado = plano;
      this.planoAcaoForm.patchValue({
        perguntaId: plano.perguntaId,
        what: plano.what,
        why: plano.why,
        where: plano.where,
        when: plano.when,
        who: plano.who,
        how: plano.how,
        howMuch: plano.howMuch,
        status: plano.status
      });
    } else {
      this.planoSelecionado = null;
      this.planoAcaoForm.reset({
        status: 'Pendente'
      });
      
      if (pergunta) {
        this.planoAcaoForm.patchValue({
          perguntaId: pergunta.id
        });
      }
    }
    this.modalService.open(modal, {
      backdrop: 'static',
      keyboard: false,
      size: 'lg'
    });
  }

  salvarPlanoAcao() {
    if (this.planoAcaoForm.valid) {
      this.util.showLoading();
      const plano = this.planoAcaoForm.value;
      
      // Adiciona a descrição da pergunta selecionada
      const perguntaSelecionada = this.perguntas.find(p => p.id === plano.perguntaId);
      if (perguntaSelecionada) {
        plano.pergunta = perguntaSelecionada.descricao;
      }
      
      if (this.planoSelecionado) {
        // Atualizar plano existente
        plano.id = this.planoSelecionado.id;
        this.ajax.put(`${API}PlanoAcao/PlanoAcao/${plano.id}`, plano)
          .subscribe({
            next: (response) => {
              this.util.closeLoading();
              if (response && response.data) {
                this.util.toasterShowSuccess("Sucesso", "Plano de ação atualizado com sucesso!");
                this.modalService.dismissAll();
                this.carregarPlanosAcao();
              } else {
                this.util.toasterShowError("Erro", "Erro ao atualizar plano de ação. Tente novamente.");
              }
            },
            error: (error) => {
              this.util.closeLoading();
              this.util.toasterShowError("Erro", error.error.message || "Erro ao atualizar plano de ação. Tente novamente.");
              console.error('Erro ao atualizar plano de ação:', error);
            }
          });
      } else {
        // Criar novo plano
        this.ajax.post(`${API}PlanoAcao/PlanoAcao`, plano)
          .subscribe({
            next: (response) => {
              this.util.closeLoading();
              if (response && response.data) {
                this.util.toasterShowSuccess("Sucesso", "Plano de ação criado com sucesso!");
                this.modalService.dismissAll();
                this.carregarPlanosAcao();
              } else {
                this.util.toasterShowError("Erro", "Erro ao criar plano de ação. Tente novamente.");
              }
            },
            error: (error) => {
              this.util.closeLoading();
              this.util.toasterShowError("Erro", error.error.message || "Erro ao criar plano de ação. Tente novamente.");
              console.error('Erro ao criar plano de ação:', error);
            }
          });
      }
    } else {
      this.util.toasterShowError("Atenção", "Por favor, preencha todos os campos obrigatórios.");
    }
  }

  excluirPlanoAcao(plano: PlanoAcao) {
    if (confirm('Tem certeza que deseja excluir este plano de ação?')) {
      this.util.showLoading();
      this.ajax.delete(`${API}PlanoAcao/PlanoAcao/${plano.id}`)
        .subscribe({
          next: (response) => {
            this.util.closeLoading();
            if (response && response.data) {
              this.util.toasterShowSuccess("Sucesso", "Plano de ação excluído com sucesso!");
              this.carregarPlanosAcao();
            } else {
              this.util.toasterShowError("Erro", "Erro ao excluir plano de ação. Tente novamente.");
            }
          },
          error: (error) => {
            this.util.closeLoading();
            this.util.toasterShowError("Erro", error.error.message || "Erro ao excluir plano de ação. Tente novamente.");
            console.error('Erro ao excluir plano de ação:', error);
          }
        });
    }
  }
} 